import React, { useState, useEffect, useCallback, useRef } from 'react';
import Dashboard from './Dashboard';
import Topics from './Topics';
import MCQ from './MCQ';
import Timetable from './Timetable';
import MCQScraper from './MCQScraper';
import Recall from './Recall';
import Feedback from './Feedback';
import Settings from './Settings';
import Changelog from './Changelog';
import ProgressChart from './ProgressChart';
import PracticeExams from './PracticeExams';
import SRAQuestions from './SRAQuestions';
import Onboarding from './Onboarding';
import TourOverlay from './AppTour';
import { HelpImportBarbri, HelpImportLawDrills } from './HelpPages';
import { LATEST_CHANGELOG_DATE } from './Changelog';
import { Lightning, Close, ChevronUp, ChevronDown, Moon, Sun, Download, Logout } from './Icons';
import './styles.css';

const API = process.env.REACT_APP_API_URL || '';

export function api(path, opts = {}) {
  const token = localStorage.getItem('sqetrack_token') || '';
  return fetch(API + path, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    ...opts,
  }).then(r => {
    if (r.status === 401) {
      localStorage.removeItem('sqetrack_token');
      window.location.href = '/login';
      return {};
    }
    return r.json();
  });
}

// Fire-and-forget event tracker — never throws
export function trackEvent(event, properties = {}) {
  api('/api/events', {
    method: 'POST',
    body: JSON.stringify({ event, properties }),
  }).catch(() => {});
}

export function downloadExport() {
  const token = localStorage.getItem('sqetrack_token') || '';
  fetch(API + '/api/export', {
    headers: { 'Authorization': `Bearer ${token}` },
  })
    .then(r => r.blob())
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SQE_Revision_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
}

function AlertBanner({ alerts, onDismiss }) {
  if (!alerts || alerts.length === 0) return null;
  return (
    <div className="alert-banner">
      {alerts.map(a => (
        <div key={a.id} className="alert-item">
          <span className="alert-icon"><Lightning size={14} /></span>
          <span className="alert-text">{a.message}</span>
          <button className="alert-dismiss" onClick={() => onDismiss(a.id)}><Close size={12} /></button>
        </div>
      ))}
    </div>
  );
}

const NAV_GROUPS = [
  { key: 'overview',  label: 'Overview',  tabs: ['dashboard'] },
  { key: 'study',     label: 'Study',     tabs: ['flk1', 'flk2', 'recall'] },
  { key: 'practice',  label: 'Practice',  tabs: ['sra', 'mcq', 'mcqresults', 'exams'] },
  { key: 'planner',   label: 'Planner',   tabs: ['timetable'] },
  { key: 'progress',  label: 'Progress',  tabs: ['progress'] },
  { key: 'more',      label: 'More',      tabs: ['feedback', 'changelog', 'settings'] },
];

const TAB_LABELS = {
  dashboard: 'Overview', flk1: 'FLK1', flk2: 'FLK2', recall: 'Recall Blurt',
  mcq: 'MCQ Log', mcqresults: 'MCQ Results', exams: 'Mock Exams',
  sra: 'SRA Questions',
  timetable: 'Planner', progress: 'Progress',
  feedback: 'Feedback', changelog: "What's new", settings: 'Settings',
};

function GroupedNav({ tab, setTab, changelogNew, feedbackBadge }) {
  const [openGroup, setOpenGroup] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const navRef = useRef(null);
  const buttonRefs = useRef({});

  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenGroup(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleGroup = (key) => {
    if (openGroup === key) { setOpenGroup(null); return; }
    const btn = buttonRefs.current[key];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpenGroup(key);
  };

  return (
    <nav className="header-nav" ref={navRef}>
      {NAV_GROUPS.map(group => {
        const isActive = group.tabs.includes(tab);
        const isSingle = group.tabs.length === 1;
        const hasNew = group.key === 'more' && (changelogNew || feedbackBadge);

        if (isSingle) {
          return (
            <button key={group.key}
              className={`nav-btn ${isActive ? 'active' : ''}`}
              onClick={() => { setTab(group.tabs[0]); setOpenGroup(null); }}>
              {group.label}
            </button>
          );
        }

        return (
          <div key={group.key} className="nav-group">
            <button
              ref={el => buttonRefs.current[group.key] = el}
              className={`nav-btn nav-group-btn ${isActive ? 'active' : ''} ${openGroup === group.key ? 'nav-group-open' : ''}`}
              onClick={() => toggleGroup(group.key)}>
              {isActive ? TAB_LABELS[tab] : group.label}
              {hasNew && !isActive && <span className="nav-new-dot" />}
              <span className="nav-group-arrow">{openGroup === group.key ? <ChevronUp size={10} /> : <ChevronDown size={10} />}</span>
            </button>
          </div>
        );
      })}
      {openGroup && (
        <div className="nav-dropdown" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
          {NAV_GROUPS.find(g => g.key === openGroup)?.tabs.map(t => (
            <button key={t}
              className={`nav-dropdown-item ${tab === t ? 'active' : ''}`}
              onClick={() => { setTab(t); setOpenGroup(null); }}>
              {TAB_LABELS[t]}
              {t === 'changelog' && changelogNew && !tab.includes('changelog') && <span className="nav-new-dot" style={{ marginLeft: 6 }} />}
              {t === 'feedback' && feedbackBadge && <span className="nav-new-dot" style={{ marginLeft: 6, background: 'var(--accent)' }} />}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'forgot-sent'

  const params = new URLSearchParams(window.location.search);
  const paymentStatus = params.get('payment');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      let recaptcha_token = '';
      try {
        recaptcha_token = await new Promise(resolve => {
          window.grecaptcha.ready(async () => {
            try {
              const t = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY || '', { action: 'login' });
              resolve(t);
            } catch { resolve(''); }
          });
        });
      } catch (_) {}
      const res = await fetch(API + '/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, recaptcha_token }),
      }).then(r => r.json());

      if (res.ok && res.token) {
        localStorage.setItem('sqetrack_token', res.token);
        window.location.href = '/app';
      } else {
        setError(res.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await fetch(API + '/api/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setView('forgot-sent');
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (view === 'forgot') {
    return (
      <div className="login-wrap">
        <div className="login-box">
          <div className="login-logo">
            <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true">
              <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
              <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
              <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
            </svg>
            <span className="login-logo-text"><span className="login-logo-sqe">SQE</span><span className="login-logo-track">Track</span></span>
          </div>
          <p className="login-sub">Reset your password</p>
          <form onSubmit={handleForgot}>
            <input
              className="login-input"
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              autoFocus
              required
            />
            {error && <p className="login-error">{error}</p>}
            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
          </form>
          <p className="login-footer">
            <button className="login-link-btn" onClick={() => setView('login')}>Back to sign in</button>
          </p>
        </div>
      </div>
    );
  }

  if (view === 'forgot-sent') {
    return (
      <div className="login-wrap">
        <div className="login-box">
          <div className="login-logo">
            <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true">
              <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
              <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
              <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
            </svg>
            <span className="login-logo-text"><span className="login-logo-sqe">SQE</span><span className="login-logo-track">Track</span></span>
          </div>
          <p className="login-sub">Check your email</p>
          <p style={{ fontSize: '.9rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            If an account exists for <strong style={{ color: 'var(--text2)' }}>{email}</strong>, you'll receive a password reset link within a few minutes.
          </p>
          <p className="login-footer">
            <button className="login-link-btn" onClick={() => setView('login')}>Back to sign in</button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-box">
        <div className="login-logo">
          <svg viewBox="0 0 32 32" width="36" height="36" aria-hidden="true">
            <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
            <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
            <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
          </svg>
          <span className="login-logo-text"><span className="login-logo-sqe">SQE</span><span className="login-logo-track">Track</span></span>
        </div>

        {paymentStatus === 'success' && (
          <div className="login-notice login-notice--success">
            Payment successful. Your account is being activated. This may take a moment.
          </div>
        )}
        {paymentStatus === 'cancelled' && (
          <div className="login-notice login-notice--error">
            Payment was cancelled. You can try again from the <a href="/">home page</a>.
          </div>
        )}

        <form onSubmit={handleLogin}>
          <input
            className="login-input"
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => { setEmail(e.target.value); setError(''); }}
            autoFocus
            required
          />
          <input
            className="login-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            required
          />
          {error && <p className="login-error">{error}</p>}
          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p className="login-footer">
          <button className="login-link-btn" onClick={() => { setView('forgot'); setError(''); }}>
            Forgot password?
          </button>
        </p>
        <p className="login-footer">
          Don't have an account? <a href="/">Get access</a>
        </p>
      </div>
    </div>
  );
}

function ResetPasswordPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="login-wrap">
        <div className="login-box">
          <div className="login-logo">
            <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true">
              <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
              <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
              <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
            </svg>
            <span className="login-logo-text"><span className="login-logo-sqe">SQE</span><span className="login-logo-track">Track</span></span>
          </div>
          <p className="login-error">Invalid reset link. Please request a new one.</p>
          <p className="login-footer"><a href="/login">Back to sign in</a></p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="login-wrap">
        <div className="login-box">
          <div className="login-logo">
            <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true">
              <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
              <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
              <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
            </svg>
            <span className="login-logo-text"><span className="login-logo-sqe">SQE</span><span className="login-logo-track">Track</span></span>
          </div>
          <div className="login-notice login-notice--success">Password updated. You can now sign in.</div>
          <p className="login-footer"><a href="/login">Sign in</a></p>
        </div>
      </div>
    );
  }

  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(API + '/api/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      }).then(r => r.json());
      if (res.ok) { setDone(true); }
      else { setError(res.error || 'Reset failed. The link may have expired.'); }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-box">
        <div className="login-logo">SQE<span className="header-title-accent">Track</span></div>
        <p className="login-sub">Set a new password</p>
        <form onSubmit={handleReset}>
          <input className="login-input" type="password" placeholder="New password (8+ characters)"
            value={password} onChange={e => { setPassword(e.target.value); setError(''); }} autoFocus required />
          <input className="login-input" type="password" placeholder="Confirm new password"
            value={confirm} onChange={e => { setConfirm(e.target.value); setError(''); }} required />
          {error && <p className="login-error">{error}</p>}
          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? 'Updating...' : 'Set new password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState('dashboard');
  const [focusTopicId, setFocusTopicId] = useState(null);
  const [lightMode, setLightMode] = useState(() => localStorage.getItem('sqe_theme') === 'light');
  const [alerts, setAlerts] = useState([]);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [onboardingDone, setOnboardingDone] = useState(true);
  const [tourDone, setTourDone] = useState(true);
  const [changelogNew, setChangelogNew] = useState(false);
  const [feedbackBadge, setFeedbackBadge] = useState(false);

  // Determine current path
  const path = window.location.pathname;
  const isLoginPage = path === '/login';
  const isResetPage = path === '/reset-password';
  const isHelpBarbri = path === '/help/import-barbri';
  const isHelpLawDrills = path === '/help/import-law-drills';
  const isAppPath = path.startsWith('/app');

  useEffect(() => {
    document.body.classList.toggle('light', lightMode);
    localStorage.setItem('sqe_theme', lightMode ? 'light' : 'dark');
  }, [lightMode]);

  // Check if JWT is valid on mount
  useEffect(() => {
    const token = localStorage.getItem('sqetrack_token');
    if (!token) {
      setChecking(false);
      return;
    }
    fetch(API + '/api/me', {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.id) {
          setAuthed(true);
          setUserEmail(data.email || '');
          setUserName(data.name || '');
          fetch(API + '/api/settings', {
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          }).then(r => r.json()).then(s => {
            // Only show onboarding for users who have been explicitly seeded with '0'
            // Existing users (no key) default to complete (true)
            setOnboardingDone(s.onboarding_complete !== '0');
            setTourDone(s.tour_shown !== '0');
            // Show "New" badge if changelog has updates since user last viewed it
            const lastViewed = s.changelog_last_viewed || '';
            setChangelogNew(LATEST_CHANGELOG_DATE > lastViewed);
          }).catch((err) => { console.error('[App] Failed to load user settings:', err); });
          fetch(API + '/api/feedback/has-unseen', {
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          }).then(r => r.json()).then(d => {
            setFeedbackBadge(d.has_unseen || false);
          }).catch((err) => { console.error('[App] Failed to load feedback badge:', err); });
        } else {
          localStorage.removeItem('sqetrack_token');
        }
      })
      .catch(() => {
        localStorage.removeItem('sqetrack_token');
      })
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (authed) {
      api('/api/alerts').then(setAlerts).catch((err) => { console.error('[App] Failed to load alerts:', err); });
      const interval = setInterval(() => api('/api/alerts').then(setAlerts).catch((err) => { console.error('[App] Alert poll failed:', err); }), 60000);
      return () => clearInterval(interval);
    }
  }, [authed]);

  // Handle URL-based routing
  useEffect(() => {
    if (!checking) {
      if (isAppPath && !authed) {
        window.location.href = '/login';
      } else if (isLoginPage && authed) {
        window.location.href = '/app';
      }
    }
  }, [checking, authed, isAppPath, isLoginPage]);

  // Handle payment=success redirect: check for activation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      const token = localStorage.getItem('sqetrack_token');
      if (token) {
        // Already logged in, check if now active
        setTab('dashboard');
      }
    }
  }, []);

  const dismissAlert = async (id) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
    await api('/api/alerts/dismiss', { method: 'POST', body: JSON.stringify({ id }) });
  };

  const navigateToTopic = useCallback((topicId, module) => {
    setFocusTopicId(topicId);
    setTab(module === 'FLK1' ? 'flk1' : 'flk2');
  }, []);

  // Legacy tooltip reset — still accessible from Settings
  const resetTooltips = useCallback(async () => {
    await api('/api/settings', { method: 'POST', body: JSON.stringify({ tour_shown: '0' }) });
    setTourDone(false);
  }, []);

  const logout = () => {
    localStorage.removeItem('sqetrack_token');
    window.location.href = '/login';
  };

  if (checking) {
    return <div className="login-wrap"><div className="login-box"><p>Loading...</p></div></div>;
  }

  if (isHelpBarbri) return <HelpImportBarbri />;
  if (isHelpLawDrills) return <HelpImportLawDrills />;

  if (isResetPage) {
    return <ResetPasswordPage />;
  }

  if (isLoginPage || (!authed && !isAppPath)) {
    return <LoginPage />;
  }

  if (!authed) {
    return <LoginPage />;
  }

  if (!onboardingDone) {
    return (
      <Onboarding
        userName={userName}
        onComplete={(name, firstTab) => {
          setUserName(name);
          setOnboardingDone(true);
          if (firstTab) setTab(firstTab);
        }}
      />
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-title">
          <svg viewBox="0 0 32 32" width="18" height="18" aria-hidden="true">
            <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
            <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
            <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
          </svg>
          <span className="header-logo-text"><span className="header-logo-sqe">SQE</span><span className="header-logo-track">Track</span></span>
        </div>
        <GroupedNav tab={tab} setTab={setTab} changelogNew={changelogNew} feedbackBadge={feedbackBadge} />
        <div className="header-actions">
          <button className="icon-btn" onClick={() => setLightMode(m => !m)} title="Toggle theme">
            {lightMode ? <Moon size={15} /> : <Sun size={15} />}
          </button>
          <button className="export-btn" onClick={downloadExport}><Download size={13} /> Export</button>
          <button className="icon-btn" onClick={logout} title={`Sign out${userName ? ` (${userName})` : ` (${userEmail})`}`}>
            <Logout size={15} />
          </button>
        </div>
      </header>

      <AlertBanner alerts={alerts} onDismiss={dismissAlert} />

      <main className="main">
        {tab === 'dashboard'   && <Dashboard onTabChange={setTab} />}
        {tab === 'flk1'        && <Topics module="FLK1" focusTopicId={focusTopicId} onFocusConsumed={() => setFocusTopicId(null)} tourHighlight={!tourDone} />}
        {tab === 'flk2'        && <Topics module="FLK2" focusTopicId={focusTopicId} onFocusConsumed={() => setFocusTopicId(null)} />}
        {tab === 'recall'      && <Recall />}
        {tab === 'timetable'   && <Timetable onNavigateToTopic={navigateToTopic} />}
        {tab === 'mcq'         && <MCQ />}
        {tab === 'mcqresults'  && <MCQScraper />}
        {tab === 'exams'       && <PracticeExams />}
        {tab === 'progress'    && <ProgressChart />}
        {tab === 'sra'         && <SRAQuestions />}
        {tab === 'feedback'    && <Feedback onViewed={() => {
          setFeedbackBadge(false);
          api('/api/settings', { method: 'POST', body: JSON.stringify({ last_feedback_viewed: new Date().toISOString() }) });
        }} />}
        {tab === 'changelog'   && <Changelog onViewed={() => setChangelogNew(false)} />}
        {tab === 'settings'    && <Settings onRestartTour={resetTooltips} />}
      </main>

      {onboardingDone && !tourDone && (
        <TourOverlay setTab={setTab} onDone={() => setTourDone(true)} />
      )}
    </div>
  );
}
